"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/Steps/DiagnosisUnlockFlow/UnlockStep/DiagnosisUnlockPlanPreview";

import { AppImage } from "@/src/components/NextImage";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import type { DiagnosisUnlockPlanPayload } from "../types";
import { shouldShowBlurredActionSummary } from "./diagnosisUnlockPlanPreviewLogic";

const I18n = I18nUtil.createScoped(Translations);

export type DiagnosisUnlockPlanPreviewProps = {
  payload: DiagnosisUnlockPlanPayload;
  /**
   * 调用方按会员态传入：`true` 隐藏「What we'll do」，`false` 非会员 blur 展示。
   */
  hideActionSummarySection?: boolean;
  className?: string;
};

/**
 * Step 3 中部 Goal + Tracker 预览 — Figma Goal Card V3 + Tracker list。
 */
export function DiagnosisUnlockPlanPreview({
  payload,
  hideActionSummarySection = false,
  className,
}: DiagnosisUnlockPlanPreviewProps) {
  const { goalTitle, goalBackgroundSrc, actionSummary, trackerSpecs } = payload;
  const showBlurredSummary = shouldShowBlurredActionSummary({
    hideActionSummarySection,
    actionSummary,
  });

  return (
    <div
      className={cn("relative w-full max-w-[300px]", className)}
      data-testid="diagnosis-unlock-plan-preview"
    >
      {/*
        Figma `90772:39973` — Goal Card V3 min-h 200px；icon↔title gap 13px；
        title 底 ↔ Tracker 顶 22px；Tracker 与 Goal 重叠 81px → content pb = 22 + 81。
      */}
      <div className="relative min-h-[200px] w-full overflow-hidden rounded-2xl border border-border_accent5_transparent50">
        <AppImage
          src={goalBackgroundSrc}
          alt=""
          width={300}
          height={200}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="relative z-[1] flex flex-col gap-[13px] px-[15px] pt-[34px] pb-[103px]">
          <div className="relative size-6 shrink-0">
            <Icon
              name="goal"
              size={24}
              className="text-content_inverse_primary"
              color={ospThemes.colors.content_inverse_primary}
            />
          </div>
          <p className="title_xl m-0 text-content_inverse_primary">
            {goalTitle}
          </p>
        </div>
      </div>
      <div className="-mt-[81px] relative z-[2] flex w-full flex-col gap-6 rounded-b-xl bg-background_solid_neutral_primary px-4 pb-6 pt-4">
        {showBlurredSummary ? (
          <div
            className="flex flex-col gap-3"
            data-testid="diagnosis-unlock-plan-action-summary"
          >
            <p className="body_medium_s text-content_tertiary">
              {I18n.t("What we'll do")}
            </p>
            <p className="body_m blur-[3px] text-content_primary">
              {actionSummary}
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <p className="body_medium_s text-content_tertiary">
            {I18n.t("What I'll do for you")}
          </p>
          <ul className="flex flex-col gap-3">
            {trackerSpecs.map((spec, index) => (
              <li
                key={`${spec.title}-${index}`}
                className="flex items-start gap-2"
              >
                <Icon
                  name="check"
                  size={16}
                  className="shrink-0 text-content_accent1_primary"
                />
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <span className="body_medium_m text-content_primary truncate">
                    {spec.title}
                  </span>
                  {spec.description ? (
                    <span className="body_m text-content_tertiary">
                      {spec.description}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
